import Cookies from 'js-cookie';
import { isNil } from 'lodash';

interface UserConfig {
    identityPoolId: string;
    subdomain: string;
    userPoolId: string;
    clientId: string;
    domain: string;
    apiEndpoint: string;
    documentRepositoryEndpoint?: string;
    documentRepositoryCoralConfig?: string;
}

const USER_CONFIG_MAP: Record<string, UserConfig> = {
    igaetan: {
        identityPoolId: 'us-east-1:4783de43-efeb-4d09-ac34-15fb4de680d4',
        subdomain: 'igaetan-pet',
        userPoolId: 'us-east-1_VLT8uG4lN',
        clientId: '1n6bq6m572c1ftbu1psasn42u9',
        domain: 'contract-manager-alpha-igaetan-pet.auth.us-east-1.amazoncognito.com',
        apiEndpoint: 'https://h6r20q007b.execute-api.us-east-1.amazonaws.com/alpha'
    },
    haggarta: {
        identityPoolId: 'us-east-1:0e735a97-4794-4635-979a-607930d5b473',
        subdomain: 'haggarta-ray',
        userPoolId: 'us-east-1_LpSscQbin',
        clientId: '518hluktokkpefue7l32m780n1',
        domain: 'contract-manager-alpha-haggarta-ray.auth.us-east-1.amazoncognito.com',
        apiEndpoint: 'https://2fhlatqilj.execute-api.us-east-1.amazonaws.com/alpha',
        documentRepositoryEndpoint: 'https://haggarta-var.personal.recordkeeper.paper.music.amazon.dev',
        documentRepositoryCoralConfig: 'alpha.us-west-2'
    },
    hxinzhuo: {
        identityPoolId: 'us-east-1:f16ab29f-2b81-4284-919e-859fecc42e31',
        subdomain: 'hxinzhuo-gae',
        userPoolId: 'us-east-1_yUWT1HM6e',
        clientId: 'brufojmsvulsuc2ksl847jcui',
        domain: 'contract-manager-alpha-hxinzhuo-gae.auth.us-east-1.amazoncognito.com',
        apiEndpoint: 'https://f7l3n3fegj.execute-api.us-east-1.amazonaws.com/alpha'
    },
    lellaom: {
        identityPoolId: 'us-east-1:8cb5353a-7d9b-4df5-9346-68965a0f8af6',
        subdomain: 'lellaom-zep',
        userPoolId: 'us-east-1_1V6nv5Axb',
        clientId: '2q4v88s13u81kn6eqdeqv0kq9q',
        domain: 'contract-manager-alpha-lellaom-zep.auth.us-east-1.amazoncognito.com',
        apiEndpoint: 'https://s8zc4rbou7.execute-api.us-east-1.amazonaws.com/alpha'
    },
    chukhang: {
        identityPoolId: 'us-east-1:cabab7c2-0b37-486e-b6f2-99d3243612f5',
        subdomain: 'chukhang-eon',
        userPoolId: 'us-east-1_LStI8oAtn',
        clientId: '37t3c6rdnkqvh54dsimhir0isa',
        domain: 'contract-manager-alpha-chukhang-eon.auth.us-east-1.amazoncognito.com',
        apiEndpoint: 'https://0r2k9gqu10.execute-api.us-east-1.amazonaws.com/alpha'
    },
    abhisuli: {
        identityPoolId: 'us-east-1:e9691bcb-165b-4f0e-af0f-86316fb5bf07',
        subdomain: 'abhisuli-sax',
        userPoolId: 'us-east-1_EvpQ6tb68',
        clientId: '37m329dbqs9l9s0dcsj3kb9ioi',
        domain: 'contract-manager-alpha-abhisuli-sax.auth.us-east-1.amazoncognito.com',
        apiEndpoint: 'https://spzt85z2h9.execute-api.us-east-1.amazonaws.com/alpha'
    }
    // Add more users as needed
};

const configDefinition = {
    personal: {
        apiEndpoint: 'https://corp.mpws-beta-0d3oeny81l.pdx.beta.hex.a2z.com'
    },
    test: {
        apiEndpoint: 'http://localhost:8280'
    },
    beta: {
        apiEndpoint: 'https://corp.mpws-beta-0d3oeny81l.pdx.beta.hex.a2z.com'
    },
    gamma: {
        apiEndpoint: 'https://corp.mpws-beta-0d3oeny81l.pdx.beta.hex.a2z.com'
    },
    prod: {
        apiEndpoint: 'https://corp.mpws-prod-9saixwq9dv.pdx.prod.hex.a2z.com'
    }
};

export function getConfigValue(path: string) {
    const stage = getStage();
    return searchObject(configDefinition, `${stage}.${path}`);
}

export function searchObject(object: any, path: string) {
    let result = object;
    const splitPath = path.split('.'),
        len = splitPath.length;
    for (let i = 0; i < len; i++) {
        result = result[splitPath[i]];
    }
    return result;
}

export function isLocalDevelopment() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '0.0.0.0' || hostname.startsWith('127.');
}

export function isPersonalLocal(): boolean {
    return isLocalDevelopment() && !isNil(getPersonalLocal());
}

export function getPersonalLocal(): string | undefined {
    return Cookies.get('LOCAL_PERSONAL_STACK');
}

export function getStage() {
    const hostname = window.location.hostname;
    if (hostname.startsWith('beta.')) return 'beta';
    else if (hostname.includes('dev-royalties.')) return 'personal';
    else if (hostname.startsWith('gamma.')) return 'gamma';
    else if (hostname.startsWith('prod.')) return 'prod';
    else if (isPersonalLocal()) return 'personal';
    else if (isLocalDevelopment()) return 'test';
    else return 'test';
}

export function getUserConfig(): UserConfig {
    const aliasFromCookie = getPersonalLocal();
    if (!isNil(aliasFromCookie)) {
        return USER_CONFIG_MAP[aliasFromCookie];
    }

    const hostname = window.location.hostname;
    // Extract user from hostname like "igaetan-pet.dev-royalties.paper.music.amazon.dev"
    const match = hostname.match(/^([^-]+)-[a-z]{3}\./);
    const user = match ? match[1] : 'haggarta'; // Default fallback
    return USER_CONFIG_MAP[user] || USER_CONFIG_MAP['igaetan'];
}

export const isOnGammaProd = () => {
    return getStage() === 'gamma' || getStage() === 'prod';
};

export const isOnTestBeta = () => {
    return getStage() === 'test' || getStage() === 'beta';
};
